import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  safePtrAccess,
  usePRFile,
  useSplitView,
} from '@sketch-hq/sketch-web-renderer'
import { useCanvasInteractions } from '@sketch/canvas'
import { relativeMeasure } from '@sketch/utils'
import { Rect } from '@sketch/inspector'

import {
  INITIAL_SPLIT_FRACTION,
  resetSplitFraction,
  setSplitFraction,
  useSplitFraction,
} from '../../useSplitFraction'
import { Handle, Wrapper } from './SplitViewSlider.styles'

/** How far an arrow key moves the line, in fractions of the entity's width. */
const KEY_STEP = 0.02

interface SplitViewSliderProps {
  /** The modified entity the line runs across. */
  entityUUID: string
  /**
   * Whether the canvas is showing the newer of the two. The older version is
   * always drawn on the left, so this decides which side the other version
   * takes - and the captions say so either way.
   */
  isViewingNewer: boolean
  /** The scene root the position is taken as a percentage of. */
  rootExtent: Rect
}

const clamp = (value: number) => Math.min(1, Math.max(0, value))

/** Somewhere the arrow keys already mean something - see `useCommandBarShortcut`. */
const isEditableTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false

  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target.isContentEditable ||
    Boolean(target.closest('[contenteditable="true"]'))
  )
}

/**
 * The line across a modified entity: the other version on its left, the
 * current one on its right.
 *
 * The line is moved by the renderer, which composites the old drawing over the
 * tiles, so dragging costs no tile work and never reads the camera. Where it
 * sits is held outside React and read back through `useSplitFraction`, so a
 * move renders this and the Split action and nothing above them. Position comes
 * from percentages of the scene root, as the entity overlays' does, and
 * `OriginContent` carries them both.
 */
export function SplitViewSlider({
  entityUUID,
  isViewingNewer,
  rootExtent,
}: SplitViewSliderProps) {
  const prFile = usePRFile()
  const { set, setFraction, clear } = useSplitView()
  const { setEnabled: setCanvasInteractions } = useCanvasInteractions()

  const wrapperRef = useRef<HTMLDivElement>(null)
  const fraction = useSplitFraction()
  /** The entity on screen, measured when a drag starts. */
  const dragBox = useRef<DOMRect | null>(null)

  const bounds = useMemo(() => {
    // The renderer may have deleted it since it was read; using it would throw
    const file = safePtrAccess(prFile)
    const rect = file?.findByUUID(entityUUID)?.getAbsoluteLayerBounds()

    return rect?.width && rect.height ? rect : null
  }, [entityUUID, prFile])

  // False when the canvas will not split it after all - one version has not
  // got it, or it is outside the scene on screen
  const [isSplit, setIsSplit] = useState(false)

  // On screen it is the newer one, so the other version goes on the left
  const sourceOnRight = !isViewingNewer

  useEffect(() => {
    resetSplitFraction()

    // Answers true for a comparison that has not reached the canvas yet, and tells us later if
    // the canvas then refuses it - without which a line would be left over nothing to split
    setIsSplit(
      set(
        { entity: entityUUID, fraction: INITIAL_SPLIT_FRACTION, sourceOnRight },
        () => setIsSplit(false)
      )
    )
  }, [entityUUID, sourceOnRight, set])

  // Through a ref, so that only going away clears - a new `clear` would
  // otherwise take the split with it
  const clearRef = useRef(clear)
  clearRef.current = clear
  useEffect(() => () => clearRef.current(), [])

  const move = useCallback(
    (next: number) => {
      const clamped = clamp(next)

      // Straight to the renderer, which redraws without touching a tile, and to
      // the one value this and the Split action both read
      setFraction(clamped)
      setSplitFraction(clamped)
    },
    [setFraction]
  )

  useEffect(() => () => resetSplitFraction(), [])

  // A drag leaves the canvas off until the pointer-up hands it back, and Escape
  // ends the split before that arrives - so going away hands it back instead
  useEffect(
    () => () => {
      if (dragBox.current) setCanvasInteractions(true)
    },
    [setCanvasInteractions]
  )

  const startDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const wrapper = wrapperRef.current
      if (!wrapper) return

      // preventDefault keeps the drag from selecting text, but it also stops the
      // handle being focused - and without focus the arrow keys go to the canvas
      event.preventDefault()
      event.currentTarget.focus()
      event.currentTarget.setPointerCapture(event.pointerId)

      // The canvas must not pan under the drag, as the left panel's resize does
      setCanvasInteractions(false)

      // Measured once: with interactions off the entity cannot move under it
      dragBox.current = wrapper.getBoundingClientRect()
    },
    [setCanvasInteractions]
  )

  const drag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const box = dragBox.current
      if (!box || box.width === 0) return

      move((event.clientX - box.left) / box.width)
    },
    [move]
  )

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!dragBox.current) return

      dragBox.current = null
      event.currentTarget.releasePointerCapture(event.pointerId)
      setCanvasInteractions(true)
    },
    [setCanvasInteractions]
  )

  /*
  The split owns the arrow keys for as long as it is up, rather than only while
  the handle has focus: the line is the one thing on screen to move, and a
  reader should not have to find a 1px handle first. Read through a ref so a
  drag does not re-register the listener on every pointer move.
  */
  const latest = useRef(fraction)
  latest.current = fraction

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const step =
        event.key === 'ArrowLeft'
          ? -KEY_STEP
          : event.key === 'ArrowRight'
            ? KEY_STEP
            : 0

      // Left to whoever is typing, and to the shortcuts a modifier belongs to
      if (!step || event.metaKey || event.ctrlKey || event.altKey) return
      if (isEditableTarget(event.target)) return

      event.preventDefault()
      move(latest.current + step)
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [move])

  if (!isSplit || !bounds || !rootExtent.width || !rootExtent.height) {
    return null
  }

  return (
    <Wrapper
      ref={wrapperRef}
      data-testid="compare-split-view"
      style={{
        left: `${relativeMeasure(bounds.x, rootExtent.width)}%`,
        top: `${relativeMeasure(bounds.y, rootExtent.height)}%`,
        width: `${relativeMeasure(bounds.width, rootExtent.width)}%`,
        height: `${relativeMeasure(bounds.height, rootExtent.height)}%`,
      }}
    >
      <Handle
        data-testid="compare-split-view-handle"
        role="slider"
        tabIndex={0}
        // The other version is the older one when the canvas shows the newer,
        // and the newer one when it shows the older
        aria-label={`Show more or less of the ${
          isViewingNewer ? 'oldest' : 'latest'
        } version`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(fraction * 100)}
        style={{ left: `${fraction * 100}%` }}
        onPointerDown={startDrag}
        onPointerMove={drag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      />
    </Wrapper>
  )
}
